'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { CheckCircle2, Zap, Users, Crown } from 'lucide-react';

const individualPlans = [
  {
    id: 'plan-setup',
    name: 'Visa Setup',
    price: '$9.99',
    priceNote: 'one-time per application',
    description: 'Get your personalized checklist and eligibility guidance — everything you need to know before you apply.',
    icon: Zap,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    highlight: false,
    ctaLabel: 'Start with Setup',
    features: [
      'AI-generated document checklist',
      'Visa eligibility assessment',
      'Embassy notes & processing times',
      'Risk warning alerts',
      'Available in 4 languages',
      'PDF checklist download',
    ],
  },
  {
    id: 'plan-full',
    name: 'Full Visa Package',
    price: '$39.99',
    priceNote: 'one-time per application',
    description: 'Complete application package — every document written, validated, and bundled for embassy submission.',
    icon: Crown,
    iconBg: 'bg-gradient-to-br from-blue-600 to-sky-500',
    iconColor: 'text-white',
    highlight: true,
    ctaLabel: 'Get Full Package',
    badge: 'Most Popular',
    features: [
      'Everything in Visa Setup',
      'AI-written cover letter',
      'Smart itinerary builder',
      'Sponsor/invitation letter',
      'Document upload & AI validation',
      'Quality & expiry checks',
      'One-click PDF bundle export',
      'Multi-language document output',
    ],
  },
];

const agencyPlans = [
  {
    id: 'plan-agency-basic',
    name: 'Agency Basic',
    price: '$299',
    priceNote: '/month',
    tokens: '200 tokens/month',
    tokenBreakdown: 'Visa Setup = 20 tokens · Full Package = 80 tokens',
    description: 'For small agencies and freelance visa consultants processing up to 10 full packages per month.',
    icon: Users,
    iconBg: 'bg-violet-50',
    iconColor: 'text-violet-600',
    highlight: false,
    ctaLabel: 'Start Agency Basic',
    features: [
      '200 tokens per month',
      'Up to 10 full packages / month',
      'Client application management',
      'White-label PDF export',
      'Priority email support',
      'Token usage analytics',
    ],
  },
  {
    id: 'plan-agency-plus',
    name: 'Agency Plus',
    price: '$999',
    priceNote: '/month',
    tokens: 'Unlimited tokens',
    tokenBreakdown: 'Process unlimited applications',
    description: 'For established agencies and large consultancies with high-volume application processing needs.',
    icon: Crown,
    iconBg: 'bg-gradient-to-br from-violet-600 to-purple-500',
    iconColor: 'text-white',
    highlight: true,
    ctaLabel: 'Start Agency Plus',
    badge: 'Unlimited',
    features: [
      'Unlimited tokens — no caps',
      'Unlimited client applications',
      'Dedicated account manager',
      'API access for integrations',
      'Custom branding & white-label',
      'Bulk PDF export & folder download',
      'Priority 24/7 support',
      'Team member seats (up to 10)',
    ],
  },
];

export default function PricingSection() {
  const [tab, setTab] = useState<'individual' | 'agency'>('individual');

  const plans = tab === 'individual' ? individualPlans : agencyPlans;

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-10 space-y-3">
          <p className="section-label">Pricing</p>
          <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 tracking-tight">
            Simple, transparent pricing
          </h2>
          <p className="text-gray-500 text-base">
            Pay only for what you need. No subscriptions for individuals — one-time payment per application.
          </p>
        </div>

        {/* Tab toggle */}
        <div className="flex justify-center mb-10">
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            {(['individual', 'agency'] as const).map((t) => (
              <button
                key={`tab-${t}`}
                onClick={() => setTab(t)}
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t === 'individual' ? '👤 Individual' : '🏢 Agency / B2B'}
              </button>
            ))}
          </div>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl border p-7 flex flex-col gap-6 transition-all duration-200 ${
                plan.highlight
                  ? 'border-blue-200 bg-gradient-to-b from-blue-50/60 to-white shadow-lg ring-2 ring-blue-100'
                  : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-card-hover'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                    {plan.badge}
                  </span>
                </div>
              )}

              {/* Plan header */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${plan.iconBg} flex items-center justify-center`}>
                    <plan.icon size={18} className={plan.iconColor} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{plan.name}</h3>
                    {'tokens' in plan && (
                      <p className="text-xs text-violet-600 font-semibold">{plan.tokens}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-extrabold text-gray-900">{plan.price}</span>
                  <span className="text-sm text-gray-400 font-medium">{plan.priceNote}</span>
                </div>

                {'tokenBreakdown' in plan && (
                  <p className="text-xs text-gray-400 font-mono bg-gray-50 rounded-lg px-3 py-1.5">{plan.tokenBreakdown}</p>
                )}

                <p className="text-sm text-gray-500 leading-relaxed">{plan.description}</p>
              </div>

              {/* Features */}
              <ul className="space-y-2.5 flex-1">
                {plan.features.map((feat) => (
                  <li key={`feat-${plan.id}-${feat.slice(0, 20)}`} className="flex items-start gap-2.5">
                    <CheckCircle2 size={15} className="text-green-500 mt-0.5 shrink-0" />
                    <span className="text-sm text-gray-600">{feat}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link
                href="/sign-up-login-screen"
                className={`w-full text-center py-2.5 rounded-xl font-semibold text-sm transition-all duration-150 active:scale-95 ${
                  plan.highlight
                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                }`}
              >
                {plan.ctaLabel} →
              </Link>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          All plans include AES-256 document encryption. Payments processed securely via Stripe.
        </p>
      </div>
    </section>
  );
}